import React from "react";
import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  NumberInput,
  BooleanInput,
  FormDataConsumer,
  required,
  maxLength,
  useRedirect,
  useNotify,
} from "react-admin";
import { Box, Typography } from "@mui/material";
import FirebaseImageInput from "../../components/FirebaseImageInput";
import ColorInput from "../../components/ColorInput";
import { ChannelActionType } from "../../types";

// Kept to one line under the channel title on phone-width cards.
const SUBTITLE_MAX_LENGTH = 60;

const actionTypeChoices = [
  { id: ChannelActionType.HANDS_UP, name: "Hands Up" },
  { id: ChannelActionType.CHAT, name: "Chat" },
  { id: ChannelActionType.DISABLED, name: "Disabled" },
];

const messageTimeoutChoices = [
  { id: 0, name: "No limit" },
  { id: 900, name: "15 minutes" },
  { id: 1800, name: "30 minutes" },
  { id: 3600, name: "1 hour" },
  { id: 10800, name: "3 hours" },
  { id: 21600, name: "6 hours" },
  { id: 43200, name: "12 hours" },
  { id: 86400, name: "1 day" },
  { id: 604800, name: "1 week" },
  { id: 2592000, name: "1 month" },
];

const ChannelCreate: React.FC = () => {
  const redirect = useRedirect();
  const notify = useNotify();

  const transform = (data: any) => ({
    id: `ra${Date.now().toString(36)}${Math.random().toString(36).substring(2, 8)}`,
    title: data.title,
    channelId: data.channelId,
    wave: data.wave || "312ft",
    streamUrl: data.streamUrl,
    subTitle: data.subTitle?.trim() || null,
    description: data.description || "",
    actionType: data.actionType || ChannelActionType.DISABLED,
    priority: data.priority ?? 0,
    isHidden: data.isHidden ?? false,
    messageTimeout: data.actionType === ChannelActionType.CHAT ? (data.messageTimeout || null) : null,
    colorHex: data.colorHex || null,
    assets: data.image ? [data.image] : [],
    shows: [],
    schedule: [],
    artists: [],
  });

  const onSuccess = () => {
    notify("channelsPage.createSuccess");
    redirect("/channels");
  };

  return (
    <Create
      resource="sync/override/channels"
      transform={transform}
      mutationOptions={{ onSuccess }}
    >
      <SimpleForm>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            ⚠️ Please note: After creating a channel, it may take up to 24 hours
            for the changes to appear in the app or on the website due to
            caching.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Changes will be reflected in the app after the next sync cycle
            (up to 6 hours) or you can trigger a manual sync from the
            dashboard.
          </Typography>
        </Box>
        <TextInput source="title" label="Title" fullWidth validate={required()} />
        <TextInput
          source="subTitle"
          label="Subtitle"
          fullWidth
          validate={maxLength(SUBTITLE_MAX_LENGTH)}
          helperText={`Short tagline shown under the channel title in the apps, e.g. "House & techno, 24/7". Max ${SUBTITLE_MAX_LENGTH} characters.`}
        />
        <TextInput source="channelId" label="Channel ID" fullWidth validate={required()} helperText="e.g. Ch.5" />
        <TextInput source="wave" label="Wave" fullWidth defaultValue="312ft" helperText="e.g. 512ft" sx={{ display: "none" }} />
        <TextInput source="streamUrl" label="Stream URL" fullWidth validate={required()} />
        <TextInput source="description" label="Description" fullWidth multiline rows={3} />
        <SelectInput
          source="actionType"
          label="Action Type"
          choices={actionTypeChoices}
          defaultValue={ChannelActionType.DISABLED}
          validate={required()}
          helperText="Controls user interaction: Disabled = no action, Chat = live chat, Hands Up = hands up icon & functionality."
          fullWidth
        />
        <FormDataConsumer>
          {({ formData }) =>
            formData?.actionType === ChannelActionType.CHAT ? (
              <SelectInput
                source="messageTimeout"
                label="Chat Message Timeout"
                choices={messageTimeoutChoices}
                defaultValue={0}
                validate={required()}
                helperText="Time before live chat messages disappear in the app."
              />
            ) : null
          }
        </FormDataConsumer>
        <NumberInput
          source="priority"
          label="Priority"
          defaultValue={0}
          helperText="Lower number = higher priority (shown first). Defaults to 0."
        />
        <BooleanInput
          source="isHidden"
          label="Hidden"
          defaultValue={false}
          helperText="Hidden channels are excluded from the public app on the next sync."
        />
        <ColorInput
          source="colorHex"
          label="Background Colour"
          helperText="Background colour used by the apps for this radio. Leave empty to use the app default."
        />
        <FirebaseImageInput source="image" label="Channel Image" storagePath="channels" />
      </SimpleForm>
    </Create>
  );
};

export default ChannelCreate;
